import React, { useEffect, useState, useCallback, useRef } from "react";

import {
    Box,
    TextField,
    Button,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Typography,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Autocomplete from "@mui/material/Autocomplete";
import axios from "axios";
import debounce from "lodash/debounce";
import { toast } from "react-toastify";
import PageHeader1 from "../../components/common/PageHeader1";
import { handleUnauthorized } from "../../TokenAuth/auth";
import { formatUTCDate } from "../../utils/dateUtils";

function AstrologerReassign() {
    // ── Table State ──
    const [rowsData, setRowsData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        page: 0,
        pageSize: 10,
    });
    const [total, setTotal] = useState(0);

    const [searchType, setSearchType] = useState("poojaName");
    const [searchValue, setSearchValue] = useState("");
    const [appliedSearch, setAppliedSearch] = useState({ type: "", value: "" });
    const [isExporting, setIsExporting] = useState(false);

    const token = localStorage.getItem("astroVani-admin-token");
    const BASE_URL = process.env.REACT_APP_BASEURL;

    // ── Fetch Rejected Poojas ──
    const fetchData = async () => {
        setLoading(true);
        try {
            const params = {
                page: pagination.page + 1,
                limit: pagination.pageSize,
                status: "Rejected", // Always fetch Rejected for reassign
            };

            if (appliedSearch.value) {
                params[appliedSearch.type] = appliedSearch.value;
            }

            const res = await axios.get(`${BASE_URL}/admin/pooja/report`, {
                params,
                headers: { Authorization: token },
            });

            setRowsData(res?.data?.data || []);
            setTotal(res?.data?.paginationDetail?.totalRecords || 0);
        } catch (err) {
            if (err?.response?.status === 401) handleUnauthorized();
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [pagination, appliedSearch, BASE_URL, token]);

    const handleApply = () => {
        setPagination({ ...pagination, page: 0 });
        setAppliedSearch({ type: searchType, value: searchValue });
    };

    const handleReset = () => {
        setSearchType("poojaName");
        setSearchValue("");
        setPagination({ ...pagination, page: 0 });
        setAppliedSearch({ type: "", value: "" });
    };

    const handleExport = async () => {
        try {
            setIsExporting(true);
            const params = { status: "Rejected" };
            if (appliedSearch.value) {
                params[appliedSearch.type] = appliedSearch.value;
            }
            const res = await axios.get(`${BASE_URL}/admin/pooja/reportExport`, {
                params,
                headers: { Authorization: token },
            });

            const data = res?.data?.data || [];
            if (!data.length) {
                toast.info("No data available to export");
                return;
            }

            const headers = [
                "S.No",
                "Order ID",
                "User Name",
                "Email",
                "Mobile Number",
                "Pooja Name",
                "Astrologer Name",
                "Status",
                "Pooja Price",
                "GST Amount",
                "Total Paid",
                "Astrologer Earnings",
                "Company Earnings",
                "Prasad Delivery",
                "Payment Status",
                "Payment Mode",
                "Wallet Credit Applied",
                "Remaining Amount",
                "Rejection Reason",
                "Created Date",
                "Created Time",
            ];

            const escapeCSV = (val) => {
                if (val === null || val === undefined) return '""';
                const str = String(val);
                return `"${str.replace(/"/g, '""')}"`;
            };

            const rows = data.map((item, i) => [
                escapeCSV(i + 1),
                escapeCSV(item.orderId || "-"),
                escapeCSV(item.userName || "Deleted User"),
                escapeCSV(item.email || "-"),
                escapeCSV(item.mobileNumber || "-"),
                escapeCSV(item.poojaName || "-"),
                escapeCSV(item.astrologerName || "-"),
                escapeCSV(item.status || "-"),
                escapeCSV(item.poojaPrice ?? 0),
                escapeCSV(item.gstAmount ?? 0),
                escapeCSV(Number(item.poojaPrice || 0) + Number(item.gstAmount || 0)),
                escapeCSV(item.astrologerEarnings ?? 0),
                escapeCSV(item.companyEarnings ?? 0),
                escapeCSV(item.prasadDelivery ? "Yes" : "No"),
                escapeCSV(item.paymentStatus || "-"),
                escapeCSV(item.paymentMode || "-"),
                escapeCSV(item.walletCreditApplied ?? 0),
                escapeCSV(item.remainingAmount ?? 0),
                escapeCSV(item.rejectionReason || "-"),
                escapeCSV(item.createdAt ? formatUTCDate(item.createdAt, { format: "date" }) : "-"),
                escapeCSV(item.createdAt ? formatUTCDate(item.createdAt, { format: "time" }) : "-"),
            ]);

            const csvContent = [headers.map(escapeCSV), ...rows].map((r) => r.join(",")).join("\n");
            const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `Astrologer_Reassign_${new Date().getTime()}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success("Export successful!");
        } catch (error) {
            if (error?.response?.status === 401) handleUnauthorized();
            console.error("Export error:", error);
            toast.error("Failed to export data");
        } finally {
            setIsExporting(false);
        }
    };

    // ── Astrologer Search State ──
    const [astrologers, setAstrologers] = useState([]);
    const [astroLoading, setAstroLoading] = useState(false);
    const [astroSearch, setAstroSearch] = useState("");
    const [astroPage, setAstroPage] = useState(1);
    const [astroPagination, setAstroPagination] = useState({
        totalDocs: 0,
        totalPages: 1,
    });

    // ── Reassign Dialog State ──
    const [mapOpen, setMapOpen] = useState(false);
    const [mapOrderId, setMapOrderId] = useState(null);
    const [mapPoojaName, setMapPoojaName] = useState("");
    const [mapRowData, setMapRowData] = useState(null);
    const mapRowDataRef = useRef(null);
    const resolvedPoojaIdRef = useRef("");
    const poojaMapRef = useRef(new Map());

    useEffect(() => {
        mapRowDataRef.current = mapRowData;
    }, [mapRowData]);

    const fetchAllPoojas = useCallback(async () => {
        try {
            const response = await axios.get(`${BASE_URL}/admin/pooja?page=1&limit=500`, {
                headers: { Authorization: token },
            });
            const list = response.data?.data?.data || response.data?.data || response.data || [];
            if (Array.isArray(list)) {
                list.forEach((p) => {
                    if (p.name && p._id) {
                        poojaMapRef.current.set(p.name.trim().toLowerCase(), p._id);
                    }
                });
            }
        } catch (e) {
            console.error("Error loading pooja list:", e);
        }
    }, [BASE_URL, token]);

    useEffect(() => {
        fetchAllPoojas();
    }, [fetchAllPoojas]);

    const [mapLoading, setMapLoading] = useState(false);
    const [selectedAstrologerId, setSelectedAstrologerId] = useState("");

    const fetchAstrologers = async (page = 1, search = "", currentPoojaId = null) => {
        try {
            setAstroLoading(true);
            const targetPoojaId =
                currentPoojaId ||
                resolvedPoojaIdRef.current ||
                mapRowDataRef.current?.poojaId?._id ||
                mapRowDataRef.current?.poojaId ||
                mapRowData?.poojaId?._id ||
                mapRowData?.poojaId;

            const finalPoojaId = typeof targetPoojaId === 'object' ? targetPoojaId?._id : targetPoojaId;
            if (!finalPoojaId) return;

            const response = await axios.get(
                `${BASE_URL}/admin/pooja/astrologers/${finalPoojaId}?page=${page}&limit=10${search ? `&search=${encodeURIComponent(search)}` : ""
                }`,
                { headers: { Authorization: token } }
            );

            const result = response.data;
            const rawList = result?.data || [];
            const mappedList = rawList.map((item) => {
                if (item.astrologerId && typeof item.astrologerId === 'object') {
                    return {
                        ...item.astrologerId,
                        _id: item.astrologerId._id,
                        mappingId: item._id,
                        fullName: item.astrologerId.fullName,
                        mobileNumber: item.astrologerId.mobileNumber,
                        profileImg: item.astrologerId.profileImg,
                        finalOfferUserPrice: item.finalOfferUserPrice,
                        finalUserPrice: item.finalUserPrice,
                        price: item.finalOfferUserPrice ?? item.finalUserPrice,
                        poojaDescription: item.poojaDescription,
                        companyShare: item.companyShare,
                        astrologerShare: item.astrologerShare,
                        rawMapping: item,
                    };
                }
                return item;
            });
            setAstrologers(mappedList);
            setAstroPagination({
                totalDocs: result?.paginationDetail?.totalDocs || 0,
                totalPages: result?.paginationDetail?.totalPages || 1,
            });
        } catch (error) {
            console.error(error);
        } finally {
            setAstroLoading(false);
        }
    };

    const debouncedAstroSearch = useCallback(
        debounce((value) => {
            setAstroPage(1);
            setAstroSearch(value);
            fetchAstrologers(1, value);
        }, 500),
        []
    );

    useEffect(() => {
        return () => debouncedAstroSearch.cancel();
    }, [debouncedAstroSearch]);

    const handleOpenMapping = async (row) => {
        setMapOrderId(row.orderId || row._id);
        setMapPoojaName(row.poojaName || "");
        setMapRowData(row);
        mapRowDataRef.current = row;
        setSelectedAstrologerId("");
        setAstroPage(1);
        setAstroSearch("");
        setMapOpen(true);

        // 1. Direct poojaId from row if available
        let pId =
            row?.poojaId?._id ||
            row?.poojaId ||
            row?.pooja?._id ||
            row?.pooja ||
            row?.pooja_id ||
            row?.pujaId?._id ||
            row?.pujaId;

        // 2. If not found, match by poojaName from poojaMap
        if (!pId && row?.poojaName) {
            const normalizedName = row.poojaName.trim().toLowerCase();
            pId = poojaMapRef.current.get(normalizedName);

            if (!pId) {
                await fetchAllPoojas();
                pId = poojaMapRef.current.get(normalizedName);
            }
        }

        // 3. Fallback: try fetching booking details by orderId / _id
        if (!pId && (row?.orderId || row?._id)) {
            try {
                const bRes = await axios.get(
                    `${BASE_URL}/admin/pooja/bookings?page=1&limit=1&searchBy=orderId&search=${encodeURIComponent(row.orderId || row._id)}`,
                    { headers: { Authorization: token } }
                );
                const booking = bRes.data?.data?.[0];
                pId = booking?.poojaId?._id || booking?.poojaId;
            } catch (err) {
                console.error("Error fetching booking details for poojaId:", err);
            }
        }

        const cleanPoojaId = typeof pId === 'object' ? pId?._id : pId;
        resolvedPoojaIdRef.current = cleanPoojaId || "";
        fetchAstrologers(1, "", cleanPoojaId);
    };

    const handleMappingSubmit = async () => {
        if (!selectedAstrologerId) {
            toast.error("Please select an Astrologer");
            return;
        }

        setMapLoading(true);
        try {
            const payload = {
                newAstrologerId: selectedAstrologerId,
            };

            // Making API call to reassign
            await axios.patch(`${BASE_URL}/admin/pooja/reassign/${mapRowData._id}`, payload, {
                headers: { Authorization: token },
            });

            toast.success("Pooja reassigned successfully!");
            setMapOpen(false);
            fetchData(); // Refresh the list
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.message || "Failed to reassign pooja");
        } finally {
            setMapLoading(false);
        }
    };

    // ── Columns ──
    const columns = [
        {
            field: "sono",
            headerName: "S.No",
            width: 80,
            sortable: false,
            filterable: false,
            renderCell: (params) =>
                params.api.getAllRowIds().indexOf(params.id) +
                1 +
                pagination.page * pagination.pageSize,
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 120,
            sortable: false,
            filterable: false,
            renderCell: ({ row }) => (
                <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    onClick={() => handleOpenMapping(row)}
                >
                    Reassign
                </Button>
            ),
        },
        { field: "orderId", headerName: "Order ID", width: 180 },
        { field: "userName", headerName: "User Name", width: 150 },
        { field: "poojaName", headerName: "Pooja Name", width: 180 },
        { field: "astrologerName", headerName: "Astrologer Name", width: 150 },
        {
            field: "createdAt",
            headerName: "Created Date",
            width: 140,
            renderCell: (params) =>
                params.value ? formatUTCDate(params.value, { format: "date" }) : "-",
        },
    ];

    return (
        <div className="container-xxl">
            <PageHeader1 pagetitle="Astrologer Reassign" />


            <div className="row g-3 mb-3">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            {/* SEARCH BAR */}
                            <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap", alignItems: "center" }}>
                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                    <InputLabel id="search-type-label" sx={{ top: '-4px' }}>Search By</InputLabel>
                                    <Select
                                        labelId="search-type-label"
                                        value={searchType}
                                        label="Search By"
                                        onChange={(e) => {
                                            setSearchType(e.target.value);
                                            setSearchValue("");
                                        }}
                                        sx={{ height: 35 }}
                                    >
                                        <MenuItem value="poojaName">Pooja Name</MenuItem>
                                        <MenuItem value="orderId">Order ID</MenuItem>
                                        <MenuItem value="mobileNumber">Mobile Number</MenuItem>
                                        <MenuItem value="userName">User Name</MenuItem>
                                        <MenuItem value="astrologerName">Astrologer Name</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField
                                    label="Search Value"
                                    value={searchValue}
                                    size="small"
                                    onChange={(e) => setSearchValue(e.target.value)}
                                    InputProps={{ sx: { height: 35 } }}
                                    InputLabelProps={{ sx: { top: '-4px' } }}
                                    sx={{ minWidth: 250 }}
                                />

                                <Button
                                    variant="contained"
                                    onClick={handleApply}
                                    sx={{ minWidth: 100, height: 35 }}
                                >
                                    Apply
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={handleReset}
                                    sx={{ minWidth: 100, height: 35 }}
                                >
                                    Reset
                                </Button>

                                <Box sx={{ flexGrow: 1 }} />
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={handleExport}
                                    disabled={isExporting}
                                    sx={{ height: 35 }}
                                >
                                    {isExporting ? "Exporting..." : "Export"}
                                </Button>
                            </Box>

                            <Box sx={{ height: 600, width: "100%" }}>
                                <DataGrid
                                    rows={rowsData}
                                    columns={columns}
                                    getRowId={(row) => row._id || Math.random()}
                                    rowCount={total}
                                    loading={loading}
                                    pageSizeOptions={[10, 25, 50]}
                                    paginationModel={pagination}
                                    paginationMode="server"
                                    onPaginationModelChange={setPagination}
                                    disableRowSelectionOnClick
                                />
                            </Box>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Reassign Dialog ── */}
            <Dialog
                open={mapOpen}
                onClose={() => setMapOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Reassign Astrologer</DialogTitle>
                <Divider />
                <DialogContent sx={{ pt: 3 }}>
                    {mapRowData && (
                        <Box mb={3} p={2} sx={{ bgcolor: "#f5f5f5", borderRadius: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="textSecondary">Order ID</Typography>
                                    <Typography fontWeight={500}>{mapRowData.orderId || "-"}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="textSecondary">Pooja Name</Typography>
                                    <Typography fontWeight={500}>{mapRowData.poojaName || "-"}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="textSecondary">User Name</Typography>
                                    <Typography fontWeight={500}>
                                        {mapRowData.userName || "N/A"} {mapRowData.mobileNumber ? `(${mapRowData.mobileNumber})` : ""}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="textSecondary">Current Astrologer</Typography>
                                    <Typography fontWeight={500}>{mapRowData.astrologerName || "None"}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="textSecondary">Pricing Details</Typography>
                                    <Typography fontWeight={500}>
                                        Price: {mapRowData.poojaPrice || 0}
                                        {/* | GST: ₹{mapRowData.gstAmount || 0 } */}

                                    </Typography>
                                </Grid>
                                {/* <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="textSecondary">Payment Details</Typography>
                                    <Typography fontWeight={500}>
                                        {mapRowData.paymentStatus?.toUpperCase() || "-"} {mapRowData.paymentMode ? `(${mapRowData.paymentMode})` : ""}
                                    </Typography>
                                </Grid> */}
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="textSecondary">Status</Typography>
                                    <Typography fontWeight={500}>
                                        {mapRowData.status || "N/A"}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    <Box
                        sx={{
                            border: "1px solid #ddd",
                            p: 2,
                            mb: 2,
                            borderRadius: 2,
                        }}
                    >
                        <Grid container spacing={2}>
                            <Box
                                width="100%"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                px={2}
                                pt={1}
                                pb={1}
                            >
                                <Button
                                    size="small"
                                    disabled={astroPage <= 1}
                                    onClick={() => {
                                        const page = astroPage - 1;
                                        setAstroPage(page);
                                        fetchAstrologers(page, astroSearch);
                                    }}
                                >
                                    Previous
                                </Button>

                                <Typography variant="body2">
                                    Page {astroPage} / {astroPagination.totalPages}
                                </Typography>

                                <Button
                                    size="small"
                                    disabled={astroPage >= astroPagination.totalPages}
                                    onClick={() => {
                                        const page = astroPage + 1;
                                        setAstroPage(page);
                                        fetchAstrologers(page, astroSearch);
                                    }}
                                >
                                    Next
                                </Button>
                            </Box>

                            <Grid item xs={12}>
                                {(() => {
                                    const selectedAstro = astrologers.find(a => (a._id === selectedAstrologerId || a.astrologerId?._id === selectedAstrologerId));
                                    const mappedPrice = selectedAstro?.finalOfferUserPrice ?? selectedAstro?.finalUserPrice ?? selectedAstro?.price;
                                    return selectedAstro && mappedPrice !== undefined && mappedPrice !== null ? (
                                        <Box mb={1}>
                                            <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                                                {`Mapped Pooja Price: ₹${mappedPrice} ${selectedAstro?.finalUserPrice && selectedAstro?.finalOfferUserPrice && selectedAstro.finalUserPrice !== selectedAstro.finalOfferUserPrice ? `(MRP: ₹${selectedAstro.finalUserPrice})` : ''}`}
                                            </Typography>
                                        </Box>
                                    ) : null;
                                })()}
                                <Autocomplete
                                    loading={astroLoading}
                                    options={astrologers}
                                    filterOptions={(x) => x}
                                    getOptionLabel={(option) => {
                                        const name = option?.fullName || option?.astrologerId?.fullName || "";
                                        const phone = option?.mobileNumber || option?.astrologerId?.mobileNumber || "-";
                                        const price = option?.finalOfferUserPrice ?? option?.finalUserPrice ?? option?.price ?? option?.videoCall?.offerPricePerMinute ?? "-";
                                        return `${name} (Phone: ${phone} | Price: ₹${price})`;
                                    }}
                                    isOptionEqualToValue={(option, val) =>
                                        (option?._id || option?.astrologerId?._id) === (val?._id || val?.astrologerId?._id)
                                    }
                                    value={
                                        astrologers.find((a) => a._id === selectedAstrologerId || a.astrologerId?._id === selectedAstrologerId) || null
                                    }
                                    onInputChange={(event, newInputValue, reason) => {
                                        if (reason === "input") {
                                            debouncedAstroSearch(newInputValue);
                                        }
                                    }}
                                    onChange={(e, value) => {
                                        setSelectedAstrologerId(value?.astrologerId?._id || value?._id || "");
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Search Astrologer"
                                            size="small"
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={() => setMapOpen(false)}
                        variant="outlined"
                        disabled={mapLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleMappingSubmit}
                        variant="contained"
                        color="success"
                        disabled={mapLoading}
                    >
                        {mapLoading ? <CircularProgress size={20} color="inherit" /> : "Submit"}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default AstrologerReassign;
